<template>
  <div :class="$style.scanner">
    <div :class="$style.header">
      <h1 :class="$style.title">{{ project ? project.title : '' }}</h1>
      <div :class="$style.modeToggle">
      </div>
    </div>

    <!-- カメラエリア -->
    <div :class="$style.cameraWrap" v-if="cameraActive">
      <qrcode-stream
        @detect="onDetect"
        @error="onCameraError"
        :track="paintQrOverlay"
        :class="$style.camera" />
      <p :class="$style.scanHint">QRコードをカメラにかざしてください</p>
      <button class="btn btn__tertiary" :class="$style.stopBtn" @click="stopCamera">カメラを閉じる</button>
    </div>

    <div v-if="!cameraActive" :class="$style.startWrap">
      <button class="btn btn__primary" @click="startCamera">スキャン開始</button>
    </div>

    <!-- エラー表示 -->
    <div v-if="cameraError" :class="$style.cameraErrorMsg">
      {{ cameraError }}
    </div>

    <!-- 処理中 -->
    <div v-if="isProcessing" :class="$style.processing">読み取り中...</div>

    <!-- エラーポップアップ（スキャン系） -->
    <div v-if="scanError" :class="[$style.popup, $style.popupError]">
      <p>{{ scanError }}</p>
    </div>

    <!-- 手動モード: 確認モーダル -->
    <div v-if="showModal && selectedJoiner" :class="$style.modal">
      <div :class="$style.modalContent">
        <p :class="$style.modalName">
          メールアドレス:{{ selectedJoiner.guest.email || '' }}
        </p>
        <p :class="[$style.modalStatus, flagClass(selectedJoiner.flag)]">
          {{ flagLabel(selectedJoiner.flag) }}
        </p>
        <div :class="$style.modalBtns">
          <button
            v-if="selectedJoiner.flag === 1"
            class="btn btn__primary"
            :disabled="isProcessing"
            @click="manualAdmit">
            入場を許可する
          </button>
          <button class="btn btn__tertiary" @click="closeModal">閉じる</button>
        </div>
      </div>
    </div>

    <!-- 自動モード: 結果ポップアップ -->
    <div
      v-if="showAutoAdmitInfo"
      :class="[$style.autoPopup, autoResult === 'admitted' ? $style.autoSuccess : $style.autoWarn]">
      <p :class="$style.autoIcon">{{ autoResult === 'admitted' ? '✓' : '!' }}</p>
      <p>{{ autoResult === 'admitted' ? '入場を許可しました' : '入場済みのチケットです' }}</p>
    </div>
  </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader';

export default {
  name: 'AdmissionScanner',
  components: { QrcodeStream },
  data() {
    return {
      project: null,
      cameraActive: false,
      cameraError: null,
      autoAdmitMode: false,
      isProcessing: false,
      selectedJoiner: null,
      showModal: false,
      showAutoAdmitInfo: false,
      autoResult: null,
      scanError: null,
      popupTimer: null,
    };
  },
  created() {
    this.loadProject();
  },
  methods: {
    loadProject() {
      // URLのprojectIdからプロジェクト情報を取得
      const projectId = Number(this.$route.params.projectId);
      // masterデータはApp.vueで読み込まれるが、未ログインのため別途取得
      this.axios({
        method: 'GET',
        url: '/v1/eventJoiner/scan',
        params: { project_id: projectId, slug: '_init_' },
      }).catch(() => {}); // 疎通確認のみ

      // プロジェクト名はlocalStorageかURLパラメータで代替（master未ロードの可能性）
      this.project = { id: projectId, title: `イベント入場管理 (ID: ${projectId})` };
    },

    paintQrOverlay(detectedCodes, ctx) {
      detectedCodes.forEach((code) => {
        const points = code.cornerPoints;
        if (!points || points.length < 4) return;

        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        points.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
        ctx.closePath();

        // 半透明の緑で塗りつぶし
        ctx.fillStyle = 'rgba(50, 205, 50, 0.35)';
        ctx.fill();

        // 枠線
        ctx.strokeStyle = 'rgba(50, 205, 50, 0.9)';
        ctx.lineWidth = 3;
        ctx.stroke();
      });
    },

    startCamera() {
      this.cameraActive = true;
      this.cameraError = null;
    },

    stopCamera() {
      this.cameraActive = false;
    },

    onCameraError(error) {
      if (error.name === 'NotAllowedError') {
        this.cameraError = 'カメラへのアクセスが拒否されました。ブラウザの設定をご確認ください。';
      } else {
        this.cameraError = `カメラエラー: ${error.message}`;
      }
      this.cameraActive = false;
    },

    onDetect(detectedCodes) {
      if (this.isProcessing || !detectedCodes.length) return;
      const raw = detectedCodes[0].rawValue;

      // URLからslugを抽出、URLでなければそのままslugとして扱う
      let slug = raw;
      try {
        const url = new URL(raw);
        slug = url.searchParams.get('slug') || raw;
      } catch (_) {
        // raw is not a URL
      }

      this.loadJoinerBySlug(slug);
    },

    async loadJoinerBySlug(slug) {
      this.isProcessing = true;
      try {
        const res = await this.axios({
          method: 'GET',
          url: '/v1/eventJoiner/scan',
          params: {
            slug,
            event_id: this.project.id,
          },
        });

        const joiner = res.data.data.data[0];
        if (!joiner) {
          this.showScanError('チケットが見つかりません。');
          return;
        }

        if (this.autoAdmitMode) {
          await this.autoAllowAdmission(joiner);
        } else {
          this.selectedJoiner = joiner;
          this.showModal = true;
          this.isProcessing = false;
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'エラーが発生しました。';
        this.showScanError(msg);
      }
    },

    async manualAdmit() {
      this.isProcessing = true;
      try {
        await this.axios({
          method: 'POST',
          url: '/v1/eventJoiner/admit',
          data: {
            id: this.selectedJoiner.id,
            event_id: this.project.id,
          },
        });
        this.closeModal();
      } catch (err) {
        alert('入場処理に失敗しました。');
      } finally {
        this.isProcessing = false;
      }
    },

    async autoAllowAdmission(joiner) {
      if (joiner.flag === 11) {
        this.showAutoResult('already_admitted');
        return;
      }
      try {
        await this.axios({
          method: 'POST',
          url: '/v1/eventJoiner/admit',
          data: { id: joiner.id, event_id: this.project.id },
        });
        this.showAutoResult('admitted');
      } catch (err) {
        this.showScanError('入場処理に失敗しました。');
      }
    },

    showAutoResult(result) {
      this.autoResult = result;
      this.showAutoAdmitInfo = true;
      clearTimeout(this.popupTimer);
      this.popupTimer = setTimeout(() => {
        this.showAutoAdmitInfo = false;
        this.isProcessing = false;
      }, 3000);
    },

    showScanError(msg) {
      this.scanError = msg;
      clearTimeout(this.popupTimer);
      this.popupTimer = setTimeout(() => {
        this.scanError = null;
        this.isProcessing = false;
      }, 3000);
    },

    closeModal() {
      this.showModal = false;
      this.selectedJoiner = null;
      this.isProcessing = false;
    },

    flagLabel(flag) {
      if (flag === 1) return '未入場';
      if (flag === 11) return '入場済み';
      if (flag === 999) return '無効';
      return '不明';
    },

    flagClass(flag) {
      if (flag === 1) return this.$style.statusOk;
      if (flag === 11) return this.$style.statusUsed;
      return this.$style.statusInvalid;
    },
  },
};
</script>

<style module lang="scss">
.scanner {
  min-height: 100vh;
  // background: #111;
  // color: #fff;
  padding: 1rem;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0 1.5rem;
}

.title {
  font-size: 1.6rem;
  font-weight: bold;
}

.modeToggle {
  font-size: 1.4rem;
  label {
    display: flex;
    align-items: center;
    gap: 0.5em;
    cursor: pointer;
  }
}

.cameraWrap {
  position: relative;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
}

.camera {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.scanHint {
  text-align: center;
  margin-top: 0.8em;
  font-size: 1.3rem;
  opacity: 0.7;
}

.stopBtn {
  display: block;
  margin: 1rem auto 0;
}

.startWrap {
  text-align: center;
  margin-top: 3rem;
}

.cameraErrorMsg {
  background: #c62828;
  padding: 1em;
  border-radius: 4px;
  margin-top: 1rem;
  font-size: 1.3rem;
}

.processing {
  text-align: center;
  margin-top: 1rem;
  opacity: 0.7;
  font-size: 1.4rem;
}

// 手動モード: モーダル
.modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modalContent {
  background: #fff;
  color: #000;
  border-radius: 8px;
  padding: 2rem;
  width: 90%;
  max-width: 360px;
  text-align: center;
}

.modalName {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.5em;
}

.modalStatus {
  font-size: 1.6rem;
  font-weight: bold;
  padding: 0.3em 1em;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 1.5em;
}

.statusOk {
  background: #e8f5e9;
  color: #2e7d32;
}

.statusUsed {
  background: #fff8e1;
  color: #f57f17;
}

.statusInvalid {
  background: #fce4ec;
  color: #c62828;
}

.modalBtns {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

// 自動モード: 全画面ポップアップ
.autoPopup {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;
  font-size: 2rem;
  font-weight: bold;
}

.autoSuccess {
  background: rgba(46, 125, 50, 0.95);
}

.autoWarn {
  background: rgba(245, 127, 23, 0.95);
}

.autoIcon {
  font-size: 5rem;
  margin-bottom: 0.5em;
}

// エラーポップアップ
.popup {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 1em 2em;
  border-radius: 6px;
  font-size: 1.4rem;
  z-index: 200;
}

.popupError {
  background: #c62828;
  color: #fff;
}
</style>
